/**
 * The transcript page's language-model actions (SEO_BRIEF_09 Part C): Translate, "Translate to
 * English", Summarize, and the captions a caption generator page sends the visitor here for.
 *
 * The markup is the server's (app/_gemini.html), rendered only when the language model can take a
 * request; without it this module finds no group and does nothing. The requests, the queue and
 * the polling are lib/gemini.ts's. This module wires the buttons, shows where a request stands,
 * paints the results and counts them (lib/usage.ts, brief 2.7).
 *
 *   * TRANSLATE. A finished translation is its own transcript, with its own page and downloads,
 *     so the page moves there (`/t/{id}`) and the tabs appear by themselves (_translation.html).
 *   * TRANSLATE TO ENGLISH. The same, in one tap. When the day's allowance is spent and the
 *     transcript came from a link, the limit banner offers speech recognition instead, which
 *     submits that link again with `translate_to: 'en'` and follows the job (2.1), and says how
 *     many free minutes that spends before anything is spent.
 *   * SUMMARIZE. The summary and its key points go in the panel above the text. On a YouTube
 *     transcript each key point's time links to that moment of the video; nothing else here has a
 *     player, so the times are plain. Copy, and Download .txt.
 *   * CAPTIONS. Three captions and hashtags for one platform, each caption in an editable box with
 *     its length against the platform's limit, counted as it is edited.
 *   * `?do=summarize` and `?do=captions&platform=…` (lib/auto-action.ts) run once by themselves,
 *     and so does `?do=translate&lang=…`, which the link's answer puts on the address when it
 *     served a caption track as it is for this page to translate (contracts/api.md §3.2): the
 *     Translate picker's own request, counted as the picker's, from the captions.
 *
 * EVERYTHING THE MODEL WROTE GOES IN AS TEXT: textContent or a textarea's value, never markup. It
 * is a stranger's transcript handed to a model, and the model's answer is just as untrusted.
 * Every word of interface copy is the template's; this module carries none.
 */

import { ApiError, submitUrl } from '../lib/api.ts';
import { autoActionFrom, withoutAutoAction } from '../lib/auto-action.ts';
import { copyText } from '../lib/clipboard.ts';
import { data, el, on, qs, qsa } from '../lib/dom.ts';
import { saveBytes, slugifyBasename } from '../lib/download.ts';
import { stamp } from '../lib/format.ts';
import {
  type CaptionsDone,
  GeminiLimitError,
  generateCaptions,
  type SummaryDone,
  summarizeTranscript,
  type TaskEvents,
  translateTranscript,
} from '../lib/gemini.ts';
import { showLimitDialog } from '../lib/quota.ts';
import {
  clearState,
  geminiLimitState,
  renderState,
  showProblem,
  showState,
} from '../lib/states.ts';
import { toast } from '../lib/toast.ts';
import { countTranslateStart, withAsked } from '../lib/translate-target.ts';
import {
  type CaptionPlatform,
  countUse,
  type GeminiFeature,
  noteResult,
  TARGET_CODE,
  type TranslateSource,
  type TranslateTrigger,
} from '../lib/usage.ts';

/** The usage counter's tool for everything on this page (pages/transcript.ts, pages/format.ts). */
const USAGE_TOOL = 'transcript';
/** A transcript is text: the format its language-model events are counted under. */
const USAGE_FORMAT = 'txt';

/* ── pure helpers (web/test/transcript-actions.test.mjs) ─────────────────── */

/** `en`, or a regional English such as `en-GB`. */
export function isEnglish(code: string | null | undefined): boolean {
  const lower = (code ?? '').trim().toLowerCase();
  return lower === 'en' || lower.startsWith('en-');
}

/** Browser tags whose language Whisper names by another code. */
const WHISPER_ALIASES: Readonly<Record<string, string>> = {
  nb: 'no',
  jv: 'jw',
  iw: 'he',
  fil: 'tl',
};

/**
 * The picker's option a browser language names (`navigator.languages` entries such as `pt-BR`,
 * `zh-TW`, `es`), or null when the picker does not offer it. Chinese written in Taiwan, Hong Kong
 * or Macau, or tagged Hant, is Traditional.
 */
export function browserTarget(tag: string, options: readonly string[]): string | null {
  const parts = tag.trim().toLowerCase().split(/[-_]/);
  const base = parts[0] ?? '';
  if (base === '') return null;
  if (base === 'zh') {
    const traditional = parts.some((p) => p === 'hant' || p === 'tw' || p === 'hk' || p === 'mo');
    const want = traditional ? 'zh-Hant' : 'zh';
    return options.includes(want) ? want : null;
  }
  const code = WHISPER_ALIASES[base] ?? base;
  return options.includes(code) ? code : null;
}

/**
 * The picker's first choice: the browser's language (its first preference the picker offers),
 * else the first option, which is the first common target that is not the transcript's own
 * language, because the server leaves that one out and keeps the brief's order (2.3).
 */
export function defaultTarget(
  options: readonly string[],
  languages: readonly string[],
): string | null {
  for (const tag of languages) {
    const hit = typeof tag === 'string' ? browserTarget(tag, options) : null;
    if (hit !== null) return hit;
  }
  return options[0] ?? null;
}

/** A time a key point may carry: a finite number of seconds, not negative. */
function isTime(seconds: unknown): seconds is number {
  return typeof seconds === 'number' && Number.isFinite(seconds) && seconds >= 0;
}

/**
 * A key point's time as it reads: whole seconds, rounded down like the link's `t=`, so the
 * time shown is the second the video opens at.
 */
function pointTime(seconds: number): string {
  return stamp(Math.floor(seconds));
}

/** A YouTube video id, as the server checks it (routes_pages `_YOUTUBE_ID`). */
const YOUTUBE_ID = /^[A-Za-z0-9_-]{11}$/;

/** Where a key point's time links on a YouTube transcript, or null for a plain time. */
export function keyPointHref(youtubeId: string | null, seconds: number): string | null {
  if (youtubeId === null || !YOUTUBE_ID.test(youtubeId) || !isTime(seconds)) return null;
  return `https://www.youtube.com/watch?v=${youtubeId}&t=${Math.floor(seconds)}s`;
}

/**
 * The summary as plain text, for Copy and the .txt: the summary, then the key points under the
 * panel's own heading, each after its time.
 */
export function summaryText(
  summary: Pick<SummaryDone, 'summary' | 'key_points'>,
  heading: string,
): string {
  const lines = [summary.summary.trim()];
  if (summary.key_points.length > 0) {
    lines.push('', heading.trim());
    for (const point of summary.key_points) {
      const text = point.text.trim();
      lines.push(isTime(point.s) ? `[${pointTime(point.s)}] ${text}` : text);
    }
  }
  return `${lines.join('\n')}\n`;
}

/** The summary's file name: the transcript's title, then `summary.txt`. */
export function summaryFilename(title: string): string {
  return `${slugifyBasename(title, 'transcript')}.summary.txt`;
}

/**
 * The whole hours a summary covers when the length cap cut its transcript (brief 2.4), at least
 * one; null when it covers all of it.
 */
export function coversHours(coversS: number | null | undefined): number | null {
  if (typeof coversS !== 'number' || !Number.isFinite(coversS) || coversS <= 0) return null;
  return Math.max(1, Math.floor(coversS / 3600));
}

/**
 * Code points X counts as one character; it counts every other one (CJK, emoji) as two. The
 * server's ranges (server/pipeline/gemini.py `_X_SINGLE`), so an edited caption's count agrees
 * with the one the server checked.
 */
const X_SINGLE: readonly (readonly [number, number])[] = [
  [0, 4_351],
  [8_192, 8_205],
  [8_208, 8_223],
  [8_242, 8_247],
];

/**
 * A caption's length as its platform counts it: characters (code points, so an emoji is one),
 * and on X the weighted count.
 */
export function captionChars(text: string, platform = ''): number {
  let count = 0;
  for (const ch of text) {
    if (platform !== 'x') {
      count += 1;
      continue;
    }
    const code = ch.codePointAt(0) ?? 0;
    count += X_SINGLE.some(([lo, hi]) => code >= lo && code <= hi) ? 1 : 2;
  }
  return count;
}

/** The hashtags as one line to paste: each once, each with one `#`. */
export function hashtagLine(tags: readonly string[]): string {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const tag of tags) {
    const bare = String(tag).trim().replace(/^#+/, '').replace(/\s+/g, '');
    if (bare === '' || seen.has(bare.toLowerCase())) continue;
    seen.add(bare.toLowerCase());
    out.push(`#${bare}`);
  }
  return out.join(' ');
}

/* ── the page ─────────────────────────────────────────────────────────── */

/** Where an action shows its progress and its refusals, and the button that started it. */
interface Surface {
  readonly button: HTMLButtonElement | null;
  /** Shown in `status` while the action runs, when there is no button to carry it. */
  readonly busyText: string | null;
  readonly status: HTMLElement | null;
  readonly region: HTMLElement;
}

export function mountTranscriptActions(root: HTMLElement, publicId: string): void {
  const group = qs<HTMLElement>('[data-gemini-actions]');
  const ready = data(root, 'translate-source') !== null;
  if (!group || !ready) return;

  const source: TranslateSource =
    data(root, 'translate-source') === 'captions' ? 'captions' : 'transcript';
  const youtubeId = data(root, 'youtube-id');
  const hasSpeech = Number(data(root, 'segment-count') ?? '1') > 0;

  const rowRegion = qs<HTMLElement>('#gemini-region', group);
  if (!rowRegion) return;
  // Typed once here, so the function declarations below (which TypeScript does not narrow
  // into) see them as present.
  const groupRegion: HTMLElement = rowRegion;
  const topRegion: HTMLElement = qs<HTMLElement>('#banner-region') ?? groupRegion;
  const groupStatus = qs<HTMLElement>('[data-gemini-status]', group);
  const topStatus = qs<HTMLElement>('[data-gemini-top-status]');

  const enButton = qs<HTMLButtonElement>('[data-translate-en]', group);
  const openButton = qs<HTMLButtonElement>('[data-translate-open]', group);
  const picker = qs<HTMLElement>('[data-translate-panel]', group);
  const choice = qs<HTMLSelectElement>('[data-translate-choice]', group);
  const goButton = qs<HTMLButtonElement>('[data-translate-go]', group);
  const summarizeButton = qs<HTMLButtonElement>('[data-summarize]', group);
  const actionButtons = [enButton, openButton, goButton, summarizeButton].filter(
    (b): b is HTMLButtonElement => b !== null,
  );

  const summaryPanel = qs<HTMLElement>('[data-summary-panel]');
  const captionsPanel = qs<HTMLElement>('[data-captions-panel]');

  let busy = false;
  /** The link a refused "Translate to English" can be done from by speech recognition. */
  let fallbackUrl: string | null = null;
  /** How that translation was asked for, which its fallback is counted under. */
  let fallbackTrigger: TranslateTrigger = 'en_button';
  let lastSummary: SummaryDone | null = null;
  /** The last action, for a refusal banner's "Try again" (NETWORK's). */
  let retry: (() => void) | null = null;
  const idleLabels = new Map(actionButtons.map((b) => [b, b.textContent ?? '']));

  /* ── progress ──────────────────────────────────────────────────────── */

  function say(status: HTMLElement | null, content: Node | string | null): void {
    if (!status) return;
    if (content === null || content === '') {
      status.replaceChildren();
      if (status === topStatus) status.hidden = true;
      return;
    }
    status.replaceChildren(content);
    status.hidden = false;
  }

  /** Disable every action and put the busy label up; returns the undo. */
  function begin(surface: Surface): () => void {
    busy = true;
    fallbackUrl = null;
    clearState(groupRegion);
    if (topRegion !== groupRegion) clearState(topRegion);
    for (const button of actionButtons) button.disabled = true;
    const button = surface.button;
    const idle = button?.textContent ?? '';
    const busyLabel = button ? data(button, 'busy-label') : null;
    if (button && busyLabel) button.textContent = busyLabel;
    say(surface.status, surface.busyText);
    return () => {
      busy = false;
      for (const b of actionButtons) b.disabled = false;
      if (button && busyLabel) button.textContent = idle;
      say(surface.status, null);
    };
  }

  /**
   * The task's progress callbacks. `accept` runs once, the first time the server shows it took
   * the request (queued, running, or done at once from the cache): that is when a `*_start`
   * usage event is counted, as Part B counts one when the server takes a request.
   */
  function progress(surface: Surface, onAccept: () => void): { on: TaskEvents; accept(): void } {
    let accepted = false;
    const accept = () => {
      if (accepted) return;
      accepted = true;
      onAccept();
    };
    return {
      accept,
      on: {
        queued() {
          accept();
          say(surface.status, renderState('GEMINI_QUEUED'));
        },
        running() {
          accept();
          say(surface.status, surface.busyText);
        },
      },
    };
  }

  /** A refusal: the limit (counted, and its fallback kept), a problem, or nothing for an abort. */
  function refused(err: unknown, feature: GeminiFeature, surface: Surface): void {
    if (err instanceof GeminiLimitError) {
      const scope = err.limit.scope;
      if (scope === 'global' || scope === 'visitor') {
        countUse('gemini_limit_hit', USAGE_TOOL, USAGE_FORMAT, { feature, scope });
      }
      const fallback = err.limit.fallback;
      fallbackUrl = fallback && typeof fallback.url === 'string' ? fallback.url : null;
      const state = geminiLimitState(err.limit);
      showState(surface.region, state.name, state.values);
      return;
    }
    if (err instanceof ApiError) {
      showProblem(surface.region, err.problem);
      return;
    }
    if (err instanceof DOMException && err.name === 'AbortError') return;
    throw err;
  }

  /* ── translate ─────────────────────────────────────────────────────── */

  function translate(
    target: string,
    trigger: 'picker' | 'en_button',
    surface: Surface,
    from: TranslateSource = source,
  ): void {
    if (busy || !TARGET_CODE.test(target)) return;
    retry = () => translate(target, trigger, surface, from);
    const done = begin(surface);
    const task = progress(surface, () =>
      countUse('translate_start', USAGE_TOOL, USAGE_FORMAT, {
        target,
        engine: 'gemini',
        source: from,
        trigger,
      }),
    );
    translateTranscript(publicId, target, trigger, task.on)
      .then((result) => {
        task.accept();
        countUse('translate_done', USAGE_TOOL, USAGE_FORMAT, { target, engine: 'gemini' });
        // The translation is a transcript of its own: its page has the tabs and the downloads.
        // The buttons stay disabled while the page goes.
        location.assign(`/t/${encodeURIComponent(result.public_id)}`);
      })
      .catch((err: unknown) => {
        done();
        fallbackTrigger = trigger;
        refused(err, 'translate', surface);
      });
  }

  /**
   * "Translate to English with speech recognition": the transcript's link submitted again to be
   * translated into English by Whisper, which spends minutes (the banner said how many). Its
   * answer is followed like the home page's: the transcript when it is ready, else the job page.
   */
  function whisperFallback(button: HTMLButtonElement, region: HTMLElement): void {
    const url = fallbackUrl;
    if (busy || url === null) return;
    busy = true;
    button.disabled = true;
    submitUrl(url, undefined, 'en')
      .then((response) => {
        countTranslateStart(
          response.translate,
          USAGE_TOOL,
          'video',
          'link',
          response.resolved ? undefined : response.job_id,
          fallbackTrigger,
        );
        location.assign(
          response.resolved
            ? withAsked(response.transcript_url, response.translate)
            : response.job_url,
        );
      })
      .catch((err: unknown) => {
        busy = false;
        button.disabled = false;
        if (!(err instanceof ApiError)) throw err;
        if (err.problem.code === 'QUOTA_EXHAUSTED') {
          showLimitDialog({
            budgetSeconds: err.problem.quota?.asr_seconds_budget,
            resetsInS: err.problem.quota?.resets_in_s ?? null,
          });
          return;
        }
        showProblem(region, err.problem);
      });
  }

  // The buttons inside a banner: the speech-recognition offer, and NETWORK's "Try again".
  for (const region of new Set([groupRegion, topRegion])) {
    on(region, 'click', (ev) => {
      const target = ev.target instanceof Element ? ev.target.closest('button') : null;
      if (!(target instanceof HTMLButtonElement)) return;
      const action = data(target, 'action');
      if (action === 'whisper-fallback') whisperFallback(target, region);
      else if (action === 'retry' && retry !== null && !busy) retry();
    });
  }

  // Back to this page from the translation's (the browser's page cache keeps it as it was
  // left, mid-press): every button as it was before the press.
  if (typeof addEventListener === 'function') {
    addEventListener('pageshow', (ev) => {
      if (!(ev as PageTransitionEvent).persisted) return;
      busy = false;
      for (const [button, label] of idleLabels) {
        button.disabled = false;
        button.textContent = label;
      }
      say(groupStatus, null);
      say(topStatus, null);
    });
  }

  /* ── summarize ─────────────────────────────────────────────────────── */

  function paintSummary(summary: SummaryDone): void {
    if (!summaryPanel) return;
    lastSummary = summary;
    const text = qs<HTMLElement>('[data-summary-text]', summaryPanel);
    if (text) text.textContent = summary.summary;

    const covers = qs<HTMLElement>('[data-summary-covers]', summaryPanel);
    const hours = coversHours(summary.covers_s);
    if (covers) {
      covers.hidden = hours === null;
      for (const line of qsa<HTMLElement>('[data-covers]', covers)) {
        line.hidden = hours === null || (data(line, 'covers') === 'hour') !== (hours === 1);
      }
      const slot = qs<HTMLElement>('[data-slot="hours"]', covers);
      if (slot && hours !== null) slot.textContent = String(hours);
    }

    const points = qs<HTMLElement>('[data-summary-points]', summaryPanel);
    const pointsHeading = qs<HTMLElement>('[data-summary-points-heading]', summaryPanel);
    if (points) {
      points.replaceChildren(
        ...summary.key_points.map((point) => {
          const item = el('li', { class: 'transcript-summary__point' });
          if (isTime(point.s)) {
            const href = keyPointHref(youtubeId, point.s);
            const time = href
              ? el(
                  'a',
                  { class: 'transcript-summary__t', href, target: '_blank', rel: 'noopener' },
                  [pointTime(point.s)],
                )
              : el('span', { class: 'transcript-summary__t' }, [pointTime(point.s)]);
            item.append(time, ' ');
          }
          item.append(document.createTextNode(point.text));
          return item;
        }),
      );
      points.hidden = summary.key_points.length === 0;
      if (pointsHeading) pointsHeading.hidden = summary.key_points.length === 0;
    }
    summaryPanel.hidden = false;
  }

  function summarize(lang: string | null, surface: Surface, reveal: boolean): void {
    if (busy || !summaryPanel) return;
    if (!hasSpeech) {
      showState(surface.region, 'NO_SPEECH');
      return;
    }
    retry = () => summarize(lang, surface, reveal);
    const done = begin(surface);
    const task = progress(surface, () =>
      countUse('summary_start', USAGE_TOOL, USAGE_FORMAT, { source }),
    );
    summarizeTranscript(publicId, lang, task.on)
      .then((summary) => {
        task.accept();
        if (summary.cached) countUse('summary_cache_hit', USAGE_TOOL, USAGE_FORMAT, {});
        done();
        paintSummary(summary);
        // Pressed in the download row, far below: take the visitor to it.
        if (reveal) summaryPanel.scrollIntoView({ block: 'start', behavior: 'smooth' });
      })
      .catch((err: unknown) => {
        done();
        refused(err, 'summary', surface);
      });
  }

  if (summaryPanel) {
    const heading = () =>
      qs<HTMLElement>('[data-summary-points-heading]', summaryPanel)?.textContent ?? '';
    const copy = qs<HTMLButtonElement>('[data-summary-copy]', summaryPanel);
    if (copy) {
      on(copy, 'click', () => {
        // No await before the clipboard call: Safari drops the gesture across one.
        if (!lastSummary) return;
        void copyText(summaryText(lastSummary, heading())).then((ok) => {
          if (!ok) return;
          toast(data(copy, 'copied-message') ?? '');
          noteResult(USAGE_TOOL, 'copy');
        });
      });
    }
    const download = qs<HTMLButtonElement>('[data-summary-download]', summaryPanel);
    if (download) {
      on(download, 'click', () => {
        if (!lastSummary) return;
        const title = qs<HTMLElement>('.transcript-meta__title')?.textContent ?? '';
        saveBytes(summaryText(lastSummary, heading()), summaryFilename(title), 'txt');
        noteResult(USAGE_TOOL, 'download');
      });
    }
  }

  /* ── captions ──────────────────────────────────────────────────────── */

  function paintCount(item: HTMLElement, limit: number): void {
    const box = qs<HTMLTextAreaElement>('[data-caption-text]', item);
    const count = qs<HTMLElement>('[data-caption-count]', item);
    if (!box || !count) return;
    const chars = captionChars(box.value, captionPlatform);
    const used = qs<HTMLElement>('[data-slot="count"]', count);
    const most = qs<HTMLElement>('[data-slot="limit"]', count);
    if (used) used.textContent = String(chars);
    if (most) most.textContent = String(limit);
    if (limit > 0 && chars > limit) count.setAttribute('data-over', 'true');
    else count.removeAttribute('data-over');
  }

  let captionLimit = 0;
  let captionPlatform = '';

  function paintCaptions(result: CaptionsDone, platform: CaptionPlatform): void {
    if (!captionsPanel) return;
    captionLimit = Number.isFinite(result.limit) && result.limit > 0 ? result.limit : 0;
    captionPlatform = platform;
    const name = qs<HTMLElement>(
      `[data-caption-platform="${CSS.escape(platform)}"]`,
      captionsPanel,
    )?.textContent;
    const headingSlot = qs<HTMLElement>('#captions-heading [data-slot="platform"]', captionsPanel);
    if (headingSlot) headingSlot.textContent = name ?? platform;
    for (const item of qsa<HTMLElement>('[data-caption]', captionsPanel)) {
      const found = result.captions.find((c) => c.kind === data(item, 'caption'));
      const box = qs<HTMLTextAreaElement>('[data-caption-text]', item);
      if (box) box.value = found?.text ?? '';
      item.hidden = !found;
      paintCount(item, captionLimit);
    }
    const tags = qs<HTMLElement>('[data-caption-tags]', captionsPanel);
    const line = hashtagLine(result.hashtags);
    if (tags) tags.textContent = line;
    const block = qs<HTMLElement>('[data-caption-tags-block]', captionsPanel);
    if (block) block.hidden = line === '';
    captionsPanel.hidden = false;
  }

  function captions(platform: CaptionPlatform, lang: string | null, surface: Surface): void {
    if (busy || !captionsPanel) return;
    if (!hasSpeech) {
      showState(surface.region, 'NO_SPEECH');
      return;
    }
    retry = () => captions(platform, lang, surface);
    const done = begin(surface);
    const task = progress(surface, () =>
      countUse('caption_generate', USAGE_TOOL, USAGE_FORMAT, { platform }),
    );
    generateCaptions(publicId, platform, lang, task.on)
      .then((result) => {
        task.accept();
        done();
        paintCaptions(result, platform);
      })
      .catch((err: unknown) => {
        done();
        refused(err, 'captions', surface);
      });
  }

  if (captionsPanel) {
    for (const item of qsa<HTMLElement>('[data-caption]', captionsPanel)) {
      const box = qs<HTMLTextAreaElement>('[data-caption-text]', item);
      if (box) on(box, 'input', () => paintCount(item, captionLimit));
      const copy = qs<HTMLButtonElement>('[data-caption-copy]', item);
      if (copy && box) {
        on(copy, 'click', () => {
          if (box.value === '') return;
          void copyText(box.value).then((ok) => {
            if (!ok) return;
            toast(data(copy, 'copied-message') ?? '');
            noteResult(USAGE_TOOL, 'copy');
          });
        });
      }
    }
    const tagsCopy = qs<HTMLButtonElement>('[data-caption-tags-copy]', captionsPanel);
    const tags = qs<HTMLElement>('[data-caption-tags]', captionsPanel);
    if (tagsCopy && tags) {
      on(tagsCopy, 'click', () => {
        const line = tags.textContent ?? '';
        if (line === '') return;
        void copyText(line).then((ok) => {
          if (!ok) return;
          toast(data(tagsCopy, 'copied-message') ?? '');
          noteResult(USAGE_TOOL, 'copy');
        });
      });
    }
  }

  /* ── the buttons ───────────────────────────────────────────────────── */

  const inRow = (button: HTMLButtonElement | null): Surface => ({
    button,
    busyText: null,
    status: groupStatus,
    region: groupRegion,
  });

  if (enButton) on(enButton, 'click', () => translate('en', 'en_button', inRow(enButton)));

  if (openButton && picker && choice && goButton) {
    const options = Array.from(choice.options, (o) => o.value);
    const languages =
      typeof navigator !== 'undefined' && Array.isArray(navigator.languages)
        ? navigator.languages
        : [typeof navigator !== 'undefined' ? navigator.language : ''];
    const first = defaultTarget(options, languages);
    if (first !== null) choice.value = first;
    on(openButton, 'click', () => {
      const opening = picker.hidden;
      picker.hidden = !opening;
      openButton.setAttribute('aria-expanded', opening ? 'true' : 'false');
      if (opening) choice.focus();
    });
    on(goButton, 'click', () => translate(choice.value, 'picker', inRow(goButton)));
  }

  if (summarizeButton) {
    on(summarizeButton, 'click', () => summarize(null, inRow(summarizeButton), true));
  }

  /* ── ?do= ──────────────────────────────────────────────────────────── */

  // Once: the query comes off the address first, so a reload or a shared link does not ask
  // again. A summarizer page's visitor arrives at the top, so progress and refusals show there.
  const action = autoActionFrom(location.search);
  if (action === null) return;
  try {
    history.replaceState(
      history.state,
      '',
      withoutAutoAction(`${location.pathname}${location.search}${location.hash}`),
    );
  } catch {
    /* the address keeps its query; the action still runs this once */
  }
  const atTop = (button: HTMLButtonElement | null, busyText: string | null): Surface => ({
    button,
    busyText,
    status: topStatus,
    region: topRegion,
  });
  if (action.kind === 'summarize') {
    const busyText = summarizeButton ? data(summarizeButton, 'busy-label') : null;
    summarize(action.lang, atTop(summarizeButton, busyText), false);
  } else if (action.kind === 'translate') {
    // The caption path's hand-off: the captions this page shows, translated as the picker would.
    const label = goButton ?? enButton;
    const busyText = label ? data(label, 'busy-label') : null;
    translate(action.lang, 'picker', atTop(null, busyText), 'captions');
  } else {
    const busyText = captionsPanel ? data(captionsPanel, 'busy-label') : null;
    captions(action.platform, action.lang, atTop(null, busyText));
  }
}
